import React from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, FormViewService, ProviderAccreditationPendenciesService } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { FetchAPI, FieldAPI, FieldListAPI, FieldListValue, FormAPI, InputAPI, PanelAPI, ToastNotificationAPI, UnboxedAutocompleteAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, msg } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ReactComponent as MagnifierIcon } from '../../../resources/assets/search.svg';
import { ClinicalStaffAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import './clinical-staff.scss';
import { AccreditationSteps } from './provider-accreditation-controller';

type MyData = {
  nrSequence: number,
  ieMainSpecialty?: string | boolean,
  checkboxIeMainSpecialty?: string | boolean,
  fieldOfWork?: number,
  nrFieldOfWork?: number,
  dsFieldOfWork?: string,
  specialty?: number,
  cdSpecialty?: number,
  name: string,
  cdCode: string,
};

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
  DETAILS: 'details',
};

@Controller({ fragment: FragmentNames.CLINICAL_STAFF })
export default class ClinicalStaffController extends BaseController {

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.FETCH_FIELD_OF_WORK)
  private fetchFieldOfWork: FetchAPI;
  @Inject(Aliases.FIELD_FIELD_OF_WORK)
  private fieldOfWork: FieldAPI;
  @Inject(Aliases.UNBOXED_FIELD_OF_WORK)
  private unboxedFieldOfWork!: UnboxedAutocompleteAPI;
  @Inject(Aliases.FORM_SPECIALTY)
  private formSpecialty!: FormAPI;
  @Inject(Aliases.FORM_DOCTOR)
  private formDoctor: FormAPI;
  @Inject(Aliases.HPMS_CUSTOM_LIST_FIELD)
  private specialtiesList: FieldListAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.FIELD_TOAST)
  private fieldToast: FieldAPI;
  @Inject(Aliases.FETCH_CLINICAL_STAFF)
  private fetchClinicalStaff: FetchAPI;
  @Inject(Aliases.INPUT_FILTER)
  private inputFilter: InputAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_PENDENCIES)
  private toastPendencies: ToastNotificationAPI;
  @Inject(Aliases.PANEL_SPECIALTY_LIST)
  private panelSpecialtiesList: PanelAPI;
  @Inject(Aliases.PANEL_SPECIALTY)
  private panelSpecialty: PanelAPI;
  @Inject(Aliases.HPMS_CUSTOM_LIST_FIELD)
  private hpmsCustomListField: FieldListAPI;

  @OnMount(Aliases.INPUT_FILTER)
  onMountFilter(): void {
    this.inputFilter.setExternalAction(<MagnifierIcon className="c-module-selector__search-icon" />);
  }

  @OnMount(Aliases.FETCH_CLINICAL_STAFF)
  onMountFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqUser, nrSeqAccreditation });
    api.forceFetch();
  }

  @OnClick(Aliases.BUTTON_ADD_DOCTOR)
  onClickAdd(): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqTypeAccreditation });
    this.panelModal.setVisible(true);
  }

  @OnChange(Aliases.INPUT_FILTER)
  onEditFilter(event: ChangeEvent<any>): void {
    const params = this.fetchClinicalStaff.getParameters();
    const desc = event.value;
    this.fetchClinicalStaff.setParameters({ ...params, desc });
    this.fetchClinicalStaff.forceFetch();
  }

  @OnClose(Aliases.MODAL_ADD_NEW)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnChange(Aliases.FIELD_SPECIALTY)
  onChangeSpecialty(event: ChangeEvent<string>): void {
    this.fieldOfWork.setValue(undefined);
    const cdSpecialty = event.value;
    this.context.set({ specialtyCode: cdSpecialty });
    this.fetchFieldOfWork.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_FIELD_OF_WORK)
  OnAfterFetchFeildOfWork(event: FetchEvent): void {
    const { data } = event;
    const isEmpty = isEmptyArrayOrObject(data);
    this.unboxedFieldOfWork.setEnabled(!isEmpty);
  }

  @OnClick(Aliases.LINK_ADD)
  onSaveActionClick(): void {
    const record = this.formSpecialty.getRecord() as MyData;
    if (!isEmptyArrayOrObject(record) && record.cdSpecialty != undefined) {
      const value = [...this.specialtiesList.getValue()];
      const specialty = {
        cdSpecialty: record.cdSpecialty,
        nrFieldOfWork: record.nrFieldOfWork,
        dsMainSpecialty: record.checkboxIeMainSpecialty ? msg('pa.yes-string') : msg('pa.no-string'),
        checkboxIeMainSpecialty: record.checkboxIeMainSpecialty,
      };

      value.push(specialty);
      const reversed = [...value].reverse();
      this.specialtiesList.setValue(reversed);
      this.formSpecialty.setValue({});
    }
  }

  @OnChange(Aliases.HPMS_CUSTOM_LIST_FIELD)
  public onChangeListBeneficiarios(changeEvent: ChangeEvent<FieldListValue>): void {
    this.specialtiesList.setValue(changeEvent.value);
  }

  @OnMount(Aliases.HPMS_CUSTOM_LIST_FIELD)
  OnMountList(): void {
    this.specialtiesList.setValue([]);
  }

  @OnSubmit(Aliases.FORM_DOCTOR)
  onSubmit(): void {
    const doctor = this.formDoctor.getRecord() as unknown as MyData;
    const specialties = this.specialtiesList.getValue();
    const body = { ...doctor, specialties };
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    if (doctor.nrSequence) {
      this.updateDoctor(nrSeqUser as number, nrSeqAccreditation as number, doctor.nrSequence, body);
    } else {
      this.saveNewDoctor(nrSeqUser as number, nrSeqAccreditation as number, body);
    }
  }

  @OnClose(Aliases.TOAST_NOTIFICATION)
  onCloseToast(): void {
    this.fieldToast.setVisible(false);
  }

  @OnCancel(Aliases.FORM_DOCTOR)
  onCancelForm(): void {
    this.panelModal.setVisible(false);
  }

  @OnMount(Aliases.FORM_SPECIALTY)
  onMountFormSpecialty(event: MountEvent): void {
    const api = event.target as FormAPI;
    FormViewService.shouldEditForm(api, AccreditationSteps.CLINICAL_STAFF);
  }

  @OnMount(Aliases.FETCH_SPECIALTY)
  onMountFetchSpecialty(event: MountEvent): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const fetch = event.target as FetchAPI;
    fetch.setParameters({ nrSeqTypeAccreditation });
    fetch.forceFetch();
  }

  @OnMount(Aliases.FIELD_TOKEN_URGENCY)
  onMountTokenUrgency(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { urgency } = api.getData();
    api.setVisible(urgency === 'S');
  }

  @OnMount(Aliases.FIELD_TOKEN_COOPERATIVE)
  onMountTokenCooperative(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { cooperative } = api.getData();
    api.setVisible(cooperative === 'S');
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data as unknown as MyData;
    const action = {
      [OptionKey.EDIT]: () => this.editDoctor(nrSequence, true),
      [OptionKey.DELETE]: () => this.deleteDoctor(nrSequence),
      [OptionKey.DETAILS]: () => this.editDoctor(nrSequence, false),

    };
    if (action[key]) {
      action[key]();
    }
  }

  @OnMount(Aliases.PANEL_ADD_BUTTOM)
  onMountAddButtom(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.CLINICAL_STAFF);
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable } = data;

    const show = {
      [OptionKey.DETAILS]: editable == 'N',
      [OptionKey.EDIT]: editable == 'S',
      [OptionKey.DELETE]: editable == 'S',
    };

    shouldRender(show[key]);
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendendies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.CLINICAL_STAFF);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toastPendencies, event);
    }
  }

  private saveNewDoctor(nrSeqUser: number, nrSeqAccreditation: number, content: {}): void {

    HttpService.post(Endpoint.POST.NEW_DOCTOR, { nrSeqUser, nrSeqAccreditation }, { body: content }).then(() => {
      this.actionsPostDoctorSuccess();
      this.panelModal.setVisible(false);
      this.fetchClinicalStaff.forceFetch();
    }).catch(({ body }) => {
      catchRenderException(() => {
        const response = body;
        this.actionsPostDoctorFail(response);
      });
    });
  }

  private updateDoctor(nrSeqUser: number, nrSeqAccreditation: number, nrSeqDoctor: number, content: {}): void {

    HttpService.post(Endpoint.POST.UPDATE_DOCTOR_DATA, { nrSeqUser, nrSeqAccreditation, nrSeqDoctor }, { body: content }).then(() => {
      this.actionsPostDoctorSuccess();
      this.panelModal.setVisible(false);
      this.fetchClinicalStaff.forceFetch();
    }).catch(({ body }) => {
      const response = body;
      this.actionsPostDoctorFail(response);
    });
  }

  private actionsPostDoctorSuccess(): void {
    this.formSpecialty.setValue({});
    this.formDoctor.setValue({});
    this.specialtiesList.setValue([]);
    this.fieldToast.setVisible(true);
    this.toast.setLevel(ToastLevel.SUCCESS);
    this.toast.setMessage(msg('pa.request-success'));
  }

  private actionsPostDoctorFail(response: string): void {
    catchRenderException(() => {
      this.fieldToast.setVisible(true);
      this.toast.setLevel(ToastLevel.ERROR);
      this.toast.setMessage(response);
    });
  }

  private editDoctor(nrSequence: number, readOnly: boolean): void {
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    void HttpService.get<Record<string, Value>>(Endpoint.GET.DOCTOR, { nrSeqUser, nrSeqAccreditation, nrSeqDoctor: nrSequence })
      .then((response) => {
        this.panelModal.setVisible(true);
        this.formDoctor.setState({ editing: readOnly, disabled: !readOnly });
        if (response.body) {
          this.formDoctor.setRecord(response.body);
          if (readOnly) {
            this.formDoctor.validate();
          }
        }
      });

    void HttpService.get<Record<string, Value>[]>(Endpoint.GET.DOCTOR_SPECIALTIES, { nrSeqUser, nrSeqAccreditation, nrSeqDoctor: nrSequence })
      .then((response) => {
        this.panelModal.setVisible(true);
        this.formSpecialty.setEditing(readOnly);
        if (response.body) {
          this.specialtiesList.setValue(response.body);
          if (!readOnly) {
            if (response.body.length > 0) {
              this.panelSpecialtiesList.setVisible(false);
              this.hpmsCustomListField.setReadOnly(true);
            } else {
              this.panelSpecialty.setVisible(false);
            }
          }
        }
      });
  }

  private deleteDoctor(nrSequence: number): void {

    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    void HttpService.delete(Endpoint.DELETE.DOCTOR, { nrSeqUser, nrSeqAccreditation, nrSeqDoctor: nrSequence }).then(() => {
      this.fetchClinicalStaff.forceFetch();
    });
  }
}
